/*
 * © 2026 Ecsper Ltd. All rights reserved.
 * SPDX-License-Identifier: LicenseRef-Ecsper-Proprietary
 * Unauthorised use, copying, or distribution is strictly prohibited.
 */

import { v7 as uuidv7 } from 'uuid';

import { addCustomXmlPart, deleteCustomXmlPart, findCustomXmlPartByNs } from './custom-xml.js';
import { sha256HexPrefix16Utf8 } from './hash-utils.js';

export const PARAGRAPH_UUID_NS = 'http://ecsper.io/schema/paragraph-uuids/1';

export interface ParagraphMetadata {
  index: number;
  /** First 16 hex chars of SHA-256 of plain text */
  hash: string;
  uuid: string;
}

function escapeXmlAttr(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
}

function serializeParagraphRegistry(entries: readonly ParagraphMetadata[]): string {
  const rows = entries
    .map(
      (e) =>
        `<p index="${e.index}" hash="${escapeXmlAttr(e.hash)}" uuid="${escapeXmlAttr(e.uuid)}" />`,
    )
    .join('');
  return `<paragraphs xmlns="${PARAGRAPH_UUID_NS}">${rows}</paragraphs>`;
}

function parseParagraphRegistryXml(xml: string): ParagraphMetadata[] {
  const parser = new DOMParser();
  const doc = parser.parseFromString(xml, 'application/xml');
  const root = doc.documentElement;
  if (root === null) return [];

  let nodes = root.getElementsByTagNameNS(PARAGRAPH_UUID_NS, 'p');
  if (nodes.length === 0) {
    nodes = root.getElementsByTagName('p');
  }

  const out: ParagraphMetadata[] = [];
  for (let i = 0; i < nodes.length; i++) {
    const el = nodes.item(i);
    if (el === null) continue;
    const indexRaw = el.getAttribute('index');
    const hash = el.getAttribute('hash') ?? '';
    const uuid = el.getAttribute('uuid') ?? '';
    const index = indexRaw !== null ? Number(indexRaw) : Number.NaN;
    if (!Number.isFinite(index) || hash === '' || uuid === '') continue;
    out.push({ index, hash, uuid });
  }
  return out.sort((a, b) => a.index - b.index);
}

async function loadParagraphPlainTexts(): Promise<string[]> {
  return Word.run(async (context) => {
    const paras = context.document.body.paragraphs;
    paras.load('items');
    await context.sync();
    const items = paras.items;
    for (const p of items) {
      p.load('text');
    }
    await context.sync();
    return items.map((p) => p.text);
  });
}

/**
 * Read the current paragraph UUID registry from custom XML.
 * Returns empty array if no registry yet exists.
 */
export async function readParagraphRegistry(): Promise<ParagraphMetadata[]> {
  const part = await findCustomXmlPartByNs(PARAGRAPH_UUID_NS);
  if (part === null) return [];
  return parseParagraphRegistryXml(part.xml);
}

/**
 * Write the registry, replacing any existing custom XML part.
 */
export async function writeParagraphRegistry(entries: readonly ParagraphMetadata[]): Promise<void> {
  const existing = await findCustomXmlPartByNs(PARAGRAPH_UUID_NS);
  if (existing !== null) {
    await deleteCustomXmlPart(existing.id);
  }
  const xml = serializeParagraphRegistry(entries);
  await addCustomXmlPart(PARAGRAPH_UUID_NS, xml);
}

/**
 * Stamp all paragraphs:
 * - If a paragraph at index N has the same hash as the registry,
 *   keep its existing UUID
 * - If a paragraph at index N has a different hash, assign new UUID v7
 * - If a paragraph is new (no registry entry), assign new UUID v7
 * - If a registry entry has no matching paragraph (deleted),
 *   drop the entry
 *
 * Returns the final registry (one entry per current paragraph).
 */
export async function stampAllParagraphs(): Promise<readonly ParagraphMetadata[]> {
  const texts = await loadParagraphPlainTexts();
  const oldRegistry = await readParagraphRegistry();
  const oldByIndex = new Map(oldRegistry.map((e) => [e.index, e]));
  const next: ParagraphMetadata[] = [];

  for (let i = 0; i < texts.length; i++) {
    const plain = texts[i] ?? '';
    const hash = await sha256HexPrefix16Utf8(plain);
    const prev = oldByIndex.get(i);
    const uuid = prev !== undefined && prev.hash === hash ? prev.uuid : uuidv7();
    next.push({ index: i, hash, uuid });
  }

  await writeParagraphRegistry(next);
  return next;
}

/**
 * Look up the UUID assigned to a paragraph by its index.
 * Returns null if not stamped.
 */
export async function readParagraphUuid(paragraphIndex: number): Promise<string | null> {
  const entries = await readParagraphRegistry();
  const hit = entries.find((e) => e.index === paragraphIndex);
  return hit?.uuid ?? null;
}
